import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  forwardRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  SelectEntry,
  SelectTemplateDirective,
  createSelectOverlay,
  flatOptions,
  optionLabel,
  optionValue,
  selectEntries,
  selectListId,
} from './select-options';

/** Equal values, objects by their content. */
export const sameValue = (a: unknown, b: unknown) =>
  a === b ||
  (a !== null &&
    b !== null &&
    typeof a === 'object' &&
    typeof b === 'object' &&
    JSON.stringify(a) === JSON.stringify(b));

/**
 * A select box with these inputs: options as values or objects (optionLabel,
 * optionValue), groups (group, optionGroupLabel, optionGroupChildren and a
 * template `appSelectTemplate="group"`), a search field (filter, filterBy)
 * and a clear icon. The list hangs on a CDK overlay below the field. The
 * arrow keys move through the list, Enter chooses, Escape and Tab close it.
 */
@Component({
  selector: 'app-select',
  standalone: true,
  imports: [NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
  template: `<div
      #origin
      class="app-select {{ styleClass }}"
      [class.app-select-open]="open"
      [class.app-focus]="focused"
      [class.app-select-clearable]="showClear && !disabled"
      [class.app-disabled]="disabled"
      role="combobox"
      aria-haspopup="listbox"
      [attr.tabindex]="disabled ? null : 0"
      [attr.aria-expanded]="open"
      [attr.aria-controls]="open ? listId : null"
      [attr.aria-activedescendant]="activeId"
      [attr.aria-label]="ariaLabel"
      [attr.aria-labelledby]="ariaLabelledBy"
      [attr.aria-disabled]="disabled || null"
      (click)="toggle()"
      (keydown)="onKeydown($event)"
      (focus)="focused = true"
      (blur)="onBlur()"
    >
      @if (selected !== undefined) {
        <span
          class="app-input app-select-label"
          [class.app-select-label-empty]="!selectedLabel"
          >{{ selectedLabel || 'empty' }}</span
        >
      } @else {
        <span
          class="app-input app-select-label app-placeholder"
          [class.app-select-label-empty]="!placeholder"
          >{{ placeholder || 'empty' }}</span
        >
      }
      @if (showClear && !disabled && selected !== undefined) {
        <i
          class="app-select-clear-icon app-icon fa-solid fa-xmark"
          aria-hidden="true"
          (click)="clear($event)"
        ></i>
      }
      <div class="app-select-trigger" aria-hidden="true">
        <span
          class="app-select-trigger-icon app-icon fa-solid fa-chevron-down"
        ></span>
      </div>
    </div>
    <ng-template #panel>
      <div class="app-select-panel {{ panelStyleClass }}">
        @if (filter) {
          <div class="app-select-header">
            <div class="app-select-filter-container">
              <input
                #filterInput
                type="text"
                autocomplete="off"
                class="app-input app-select-filter"
                [value]="filterValue"
                [attr.placeholder]="filterPlaceholder"
                [attr.aria-controls]="listId"
                [attr.aria-activedescendant]="activeId"
                (input)="onFilterInput($event)"
                (keydown)="onKeydown($event)"
              />
              <span
                class="app-select-filter-icon app-icon fa-solid fa-magnifying-glass"
              ></span>
            </div>
          </div>
        }
        <div class="app-select-items-wrapper" [style.max-height]="scrollHeight">
          <ul class="app-select-items" role="listbox" [id]="listId">
            @for (entry of entries; track $index) {
              @if (entry.group) {
                <li class="app-select-item-group" role="presentation">
                  @if (groupTemplate) {
                    <ng-container
                      *ngTemplateOutlet="
                        groupTemplate;
                        context: { $implicit: entry.option }
                      "
                    ></ng-container>
                  } @else {
                    <span>{{ entry.label || 'empty' }}</span>
                  }
                </li>
              } @else {
                <!-- The select box keeps the focus and moves through the options (aria-activedescendant). -->
                <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
                <li
                  class="app-select-item"
                  role="option"
                  [id]="listId + '-' + entry.index"
                  [class.app-highlight]="entry.option === selected"
                  [class.app-select-item-active]="entry.index === activeIndex"
                  [attr.aria-selected]="entry.option === selected"
                  (click)="choose(entry.option)"
                >
                  <span>{{ entry.label || 'empty' }}</span>
                </li>
              }
            }
            @if (!optionCount) {
              <li class="app-select-empty-message">
                {{ filterValue ? emptyFilterMessage : emptyMessage }}
              </li>
            }
          </ul>
        </div>
      </div>
    </ng-template>`,
})
export class SelectComponent
  implements ControlValueAccessor, OnChanges, OnDestroy
{
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);
  private cdr = inject(ChangeDetectorRef);

  @Input() options: unknown[] | null | undefined = [];
  @Input() optionLabel?: string;
  @Input() optionValue?: string;
  @Input() group = false;
  @Input() optionGroupLabel?: string;
  @Input() optionGroupChildren?: string;
  @Input() placeholder?: string;
  @Input() filter = false;
  /** The fields the search looks at, separated by commas. */
  @Input() filterBy?: string;
  @Input() filterPlaceholder?: string;
  @Input() resetFilterOnHide = false;
  @Input() showClear = false;
  @Input() emptyMessage = 'No results found';
  @Input() emptyFilterMessage = 'No results found';
  @Input() scrollHeight = '200px';
  @Input() styleClass = '';
  @Input() panelStyleClass = '';
  @Input() ariaLabel?: string;
  /** Id of a visible label of the select box. */
  @Input() ariaLabelledBy?: string;
  @Input() disabled = false;

  /** The value a user chose, also on clearing. */
  @Output() valueChange = new EventEmitter<unknown>();

  @ViewChild('origin', { static: true })
  private origin!: ElementRef<HTMLElement>;
  @ViewChild('panel', { static: true })
  private panelTemplate!: TemplateRef<unknown>;
  @ContentChildren(SelectTemplateDirective)
  private templates?: QueryList<SelectTemplateDirective>;

  readonly listId = selectListId();
  value: unknown = null;
  /** The chosen option, undefined without one. */
  selected: unknown = undefined;
  selectedLabel = '';
  entries: SelectEntry[] = [];
  optionCount = 0;
  filterValue = '';
  activeIndex = -1;
  focused = false;

  private overlayRef: OverlayRef | null = null;
  private onChange: (value: unknown) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  get open() {
    return !!this.overlayRef?.hasAttached();
  }

  get activeId() {
    return this.open && this.activeIndex >= 0
      ? `${this.listId}-${this.activeIndex}`
      : null;
  }

  get groupTemplate() {
    return this.templates?.find((t) => t.name === 'group')?.template ?? null;
  }

  private get fields() {
    return {
      optionLabel: this.optionLabel,
      optionValue: this.optionValue,
      optionGroupLabel: this.optionGroupLabel,
      optionGroupChildren: this.optionGroupChildren,
    };
  }

  ngOnChanges(): void {
    this.updateSelected();
    this.updateEntries();
  }

  ngOnDestroy(): void {
    const ref = this.overlayRef;
    this.overlayRef = null;
    ref?.dispose();
  }

  writeValue(value: unknown): void {
    this.value = value;
    this.updateSelected();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.cdr.markForCheck();
  }

  toggle() {
    if (this.open) {
      this.hide();
    } else {
      this.show();
    }
  }

  show() {
    if (this.open || this.disabled) {
      return;
    }
    const origin = this.origin.nativeElement;
    const ref = createSelectOverlay(this.overlay, origin);
    this.overlayRef = ref;
    this.activeIndex = this.selectedIndex();
    ref.attach(new TemplatePortal(this.panelTemplate, this.viewContainerRef));
    ref.outsidePointerEvents().subscribe((event) => {
      if (!origin.contains(event.target as Node)) {
        this.hide();
      }
    });
    ref.detachments().subscribe(() => this.detached(ref));
    ref.overlayElement
      .querySelector<HTMLInputElement>('.app-select-filter')
      ?.focus();
    this.scrollToActive();
    this.cdr.markForCheck();
  }

  hide() {
    if (this.overlayRef) {
      this.detached(this.overlayRef);
    }
  }

  /** Empties the search field and shows all options again. */
  resetFilter() {
    this.filterValue = '';
    this.updateEntries();
    this.cdr.markForCheck();
  }

  choose(option: unknown) {
    this.setValue(optionValue(option, this.fields));
    this.hide();
  }

  clear(event: Event) {
    event.stopPropagation();
    this.setValue(null);
  }

  onFilterInput(event: Event) {
    this.filterValue = (event.target as HTMLInputElement).value;
    this.updateEntries();
    this.activeIndex = this.optionCount ? 0 : -1;
    this.overlayRef?.updatePosition();
  }

  onKeydown(event: KeyboardEvent) {
    if (this.disabled) {
      return;
    }
    const inFilter = event.target !== this.origin.nativeElement;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (!this.open) {
          this.show();
        } else {
          this.moveActive(event.key === 'ArrowDown' ? 1 : -1);
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (!this.open) {
          this.show();
        } else if (this.activeIndex >= 0) {
          this.choose(this.optionAt(this.activeIndex));
        } else {
          this.hide();
        }
        break;
      case ' ':
        if (!inFilter) {
          event.preventDefault();
          this.toggle();
        }
        break;
      case 'Escape':
        if (this.open) {
          event.preventDefault();
          event.stopPropagation();
          this.hide();
        }
        break;
      case 'Tab':
        this.hide();
        break;
    }
  }

  onBlur() {
    this.focused = false;
    if (!this.open) {
      this.onTouched();
    }
  }

  private setValue(value: unknown) {
    this.value = value;
    this.updateSelected();
    this.onChange(value);
    this.valueChange.emit(value);
    this.cdr.markForCheck();
  }

  private detached(ref: OverlayRef) {
    if (this.overlayRef !== ref) {
      return;
    }
    const focusInPanel = !!ref.overlayElement?.contains(document.activeElement);
    this.overlayRef = null;
    ref.dispose();
    if (this.resetFilterOnHide) {
      this.filterValue = '';
      this.updateEntries();
    }
    if (focusInPanel) {
      this.origin.nativeElement.focus({ preventScroll: true });
    }
    this.onTouched();
    this.cdr.markForCheck();
  }

  private updateSelected() {
    this.selected = flatOptions(this.options, this.group, this.fields).find(
      (option) => sameValue(optionValue(option, this.fields), this.value)
    );
    this.selectedLabel =
      this.selected === undefined
        ? ''
        : optionLabel(this.selected, this.fields);
  }

  private updateEntries() {
    this.entries = selectEntries(
      this.options,
      this.group,
      this.filterValue,
      this.filterBy,
      this.fields
    );
    this.optionCount = this.entries.filter((entry) => !entry.group).length;
  }

  private optionAt(index: number) {
    return this.entries.find((entry) => entry.index === index)?.option;
  }

  private selectedIndex() {
    return (
      this.entries.find(
        (entry) => !entry.group && entry.option === this.selected
      )?.index ?? -1
    );
  }

  private moveActive(step: number) {
    if (!this.optionCount) {
      return;
    }
    const next = this.activeIndex + step;
    this.activeIndex = Math.max(0, Math.min(this.optionCount - 1, next));
    this.cdr.detectChanges();
    this.scrollToActive();
  }

  private scrollToActive() {
    const id = this.activeId;
    if (id) {
      this.overlayRef?.overlayElement
        .querySelector(`#${id}`)
        ?.scrollIntoView?.({ block: 'nearest' });
    }
  }
}
